import React, { useState, useEffect } from 'react';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { Image as AntdImage, Upload, message, Spin, Card } from 'antd';
import { ENDPOINT } from '../../../Services/ENDPOINT';
import { LetterService } from '../../../Services/Manage/LetterService';

const normalizeUrl = (base, path) => `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;

const DigitalSign = () => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadExistingSigns();
  }, []);

  const loadExistingSigns = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${ENDPOINT.getAllDgSignDb}`);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const result = await response.json();

      if (result?.data && Array.isArray(result.data)) {
        const imageFiles = result.data.map((item, index) => ({
          uid: `db-${item.id || index}`,
          name: item.image_path.split('/').pop(),
          status: 'done',
          url: normalizeUrl(ENDPOINT.base, item.image_path),
          dbId: item.id,
        }));
        setFileList(imageFiles);
      } else {
        message.error('Failed to load signs: invalid response format');
      }
    } catch (error) {
      console.error(error);
      message.error('Error loading signs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const customUpload = ({ file, onSuccess, onError }) => {
    setLoading(true);

    // Use native Image to check dimensions
    const img = new window.Image();
    img.src = URL.createObjectURL(file);

    img.onload = async () => {
// if (img.width !== 700 || img.height !== 700) {
//   message.error('Image must be exactly 512x512 pixels.');
//   setLoading(false);
//   return onError(new Error('Invalid image dimensions'));
// }


      const formData = new FormData();
      formData.append('image', file);

      try {
        const response = await fetch(`${ENDPOINT.getAllDgSignDb}`, {
          method: 'POST',
          body: formData,
        });
        const result = await response.json();

        if (result.status && result.success) {
          onSuccess(result, file);
          const newFile = {
            uid: file.uid,
            name: result.image?.filename || file.name,
            status: 'done',
            url: normalizeUrl(ENDPOINT.base, `uploads/digitalsign/${result.image?.filename}`),
            response: result,
            dbId: result.data?.id,
          };
          setFileList(prev => [...prev, newFile]);
          message.success('Upload successful!');
        } else {
          throw new Error(result.message || 'Upload failed');
        }
      } catch (error) {
        onError(error);
        message.error('Upload failed: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    img.onerror = () => {
      message.error('Failed to load image for dimension check.');
      setLoading(false);
      onError(new Error('Failed to load image'));
    };
  };

const customRemove = async file => {
  try {
    setLoading(true);

    // Always get just the filename
    const fileName = file.response?.image?.filename || file.url?.split('/').pop();
    const imageId = file.dbId;

    let request = { status: true };
    let requestDb = { status: true };

    if (fileName) {
      request = await LetterService.deleteDgSign(fileName);
    }

    if (imageId) {
      requestDb = await LetterService.deleteDgSignDb(imageId);
    }

    if (request?.status && requestDb?.status) {
      message.success('Deleted successfully!');
      loadExistingSigns();
    } else {
      loadExistingSigns();
      throw new Error(request?.message || requestDb?.message || 'Delete failed');
    }

    return true;
  } catch (error) {
    message.error('Delete failed: ' + error.message);
    return false;
  } finally {
    setLoading(false);
  }
};


  const handlePreview = async file => {
    try {
      if (!file.url && !file.preview) {
        file.preview = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file.originFileObj);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });
      }
      setPreviewImage(file.url || file.preview);
      setPreviewOpen(true);
    } catch (error) {
      message.error('Failed to preview image: ' + error.message);
    }
  };

  const uploadButton = (
    <button style={{ border: 0, background: 'none' }} type="button" disabled={loading}>
      {loading ? <Spin indicator={<LoadingOutlined spin />} /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>{loading ? 'Uploading...' : 'Upload'}</div>
    </button>
  );

  return (
    <Card title="Upload Digital Signatures">
      <div>
        <Upload
          customRequest={customUpload}
          listType="picture-card"
          fileList={fileList}
          onPreview={handlePreview}
          onRemove={customRemove}
          accept="image/jpeg,image/jpg,image/png,image/webp"
          multiple
          maxCount={8}
          disabled={loading}
          showUploadList={{ showPreviewIcon: true, showRemoveIcon: true }}
        >
          {fileList.length >= 8 ? null : uploadButton}
        </Upload>
        <AntdImage
          style={{ display: 'none' }}
          preview={{
            visible: previewOpen,
            onVisibleChange: setPreviewOpen,
            afterOpenChange: visible => !visible && setPreviewImage(''),
          }}
          src={previewImage}
        />
      </div>
    </Card>
  );
};

export default DigitalSign;
